// Topic table columns — name tile (bookmark) + bold title, the absolute
// last-updated timestamp, the last-updated-by avatar, and the row-actions
// kebab. The kebab's menu lives in TopicRowActions — it needs its own open
// state — and its two callbacks are verb-gated by the tab: absent = hidden,
// and with neither the kebab renders nothing.

import { useTranslation } from 'react-i18next';
import { Avatar, Icon } from '@/components/common';
import type { DataTableColumn } from '@/components/common';
import type { Topic, TopicPerson } from '@/api/endpoints/topicSetsApi';
import toneOf from '@/utils/avatarTone';
import initialsOf from '@/utils/initials';
import TopicRowActions from './TopicRowActions';
import { fmtDateTime } from '@/utils/formatDate';

const PersonCell = ({ person }: { person: TopicPerson | null }) => {
  if (!person) return <span className='topic-table__muted'>—</span>;
  return (
    <span className='topic-table__person'>
      <Avatar size={28} tone={toneOf(person.fullName)} initials={initialsOf(person.fullName)} />
      {person.fullName}
    </span>
  );
};

interface UseTopicColumnsArgs {
  /** Row kebab — verb-gated by the tab: absent = hidden in the kebab (the
   *  kebab itself renders nothing when both are absent). */
  onEdit?: (topic: Topic) => void;
  onDelete?: (topic: Topic) => void;
}

export const useTopicColumns = ({
  onEdit,
  onDelete,
}: UseTopicColumnsArgs = {}): DataTableColumn<Topic>[] => {
  const { t } = useTranslation();

  return [
    // `sortable` marks exactly the confirmed TopicSortKey pair, each column's
    // `key` being the wire orderBy.key; the tab passes the matching `onSort`.
    {
      key: 'title',
      label: t('data_structure_tp_col_name'),
      width: 'minmax(16rem, 2.5fr)',
      sortable: true,
      render: (row) => (
        <span className='topic-table__name'>
          <span className='topic-table__tile' aria-hidden='true'>
            <Icon name='bookmark' size={18} />
          </span>
          <span className='topic-table__title'>{row.title}</span>
        </span>
      ),
    },
    {
      key: 'updatedAt',
      label: t('data_structure_tp_col_updated'),
      sortable: true,
      render: (row) => fmtDateTime(row.updatedAt),
    },
    {
      key: 'lastUpdatedBy',
      label: t('data_structure_tp_col_updated_by'),
      width: 'minmax(11rem, 1fr)',
      render: (row) => <PersonCell person={row.lastUpdatedBy} />,
    },
    {
      key: 'actions',
      label: '',
      width: '3rem',
      align: 'end',
      render: (row) => <TopicRowActions topic={row} onEdit={onEdit} onDelete={onDelete} />,
    },
  ];
};

export default useTopicColumns;
